import Vue from 'vue'
import Vuex from 'vuex'
import * as config from './../config/config';
import mutations from './mutations/index'
import axios from 'axios'
import localforage from "localforage";
import moment from 'moment';
import { writeLog } from "./../utils/logging";
import { writeTrackLog } from "./../utils/tracking";
import { v4 } from 'uuid';
import {isOnline} from "../utils/online";
import {markPendingDeviceSetting, clearPendingDeviceSetting, flushPendingDeviceSettings} from "../utils/pendingChanges";
import { saveNewWorkday } from "../api/workdays";
import {loadAsyncLanguages, setLanguage} from "../translations/main";
import FileCache from "@/utils/file";

Vue.use(Vuex);

// Persist a device setting: update local state right away, then push it to the
// server. While offline (or if the request fails) the setting is flagged dirty
// so flushPendingDeviceSettings() can push the current value once back online,
// before any new data is pulled. `key` must match a SETTINGS key in pendingChanges.
function persistDeviceSetting(commit, state, key, endpoint, payload) {
  commit('overrideLocal', false);

  if (isOnline()) {
    axios.put(config.api.current + endpoint, payload, {headers: {'Authorization': 'Bearer ' + state.token}})
        .then(() => {
          clearPendingDeviceSetting(key);
          commit('overrideLocal', true);
        })
        .catch(() => markPendingDeviceSetting(key));
  } else {
    markPendingDeviceSetting(key);
  }
}

const appStore = new Vuex.Store({
  state: {
    version: '1.3.14',
    fcmToken: null,
    fcmTokenSynced: false,
    language: 'en',
    debug: false,
    enableTracking: false,
    online: false,
    token: null,
    fullscreen: false,
    instantSync: false,
    syncOnline: true,
    devices: [],
    currentDevice: null,
    locations: [],
    currentLocation: null,
    currentEmployee: null,
    currentEmployeeActivities: [],
    checked: false,
    afterChecked: null,
    error: null,
    success: null,
    teams: null,
    employees: null,
    activities: null,
    currentActivity: null,
    teamsOrder: null,
    teamsAsButtons: false,
    rows: null,
    rowsAsButtons: false,
    rowsOrder: null,
    packagings: null,
    percentages: null,
    workdays: null,
    timesheetEntries: [],
    activityOrder: null,
    pinVisible: false,
    darkMode: false,
    overrideLocal: false,
    batteryCharging: false,
    batteryLevel: 0
  },

  mutations: mutations,

  actions: {
    pushLogs ({state}, payload) {
      if (!state.currentDevice) return;

      axios.post(
        config.api.current + '/device/' + state.currentDevice.id + '/logs',
        { debug: payload.debug, tracking: payload.tracking },
        { headers: { 'Authorization': 'Bearer ' + state.token } }
      ).catch(err => {
        console.error(err)
        if (state.enableTracking) writeTrackLog('Error posting logs')
      });
    },

    setFullscreen ({commit}, val) {
      commit('setFullscreen', val);
    },

    authenticateDevice ({dispatch, commit, state}, payload) {
      console.log('Store:authenticateDevice', payload)

      return localforage.getItem('overrideLocal').then(overrideLocal => {
        if (process.env.NODE_ENV === 'development') console.log('Store:authenticateDevice::override device settings?', overrideLocal)
        commit('overrideLocal', overrideLocal);
        return localforage.getItem('teams_order');
      }).then(teamsOrder => {
        if (process.env.NODE_ENV === 'development')console.log('Store:authenticateDevice::set teams order from local', teamsOrder)
        commit('setTeamsOrder', teamsOrder);
        return localforage.getItem('teamsAsButtons')
      }).then(teamsAsButtons => {
        if (process.env.NODE_ENV === 'development')console.log('Store:authenticateDevice::set teams as buttons from local', teamsAsButtons);
        if (teamsAsButtons) commit('setTeamsAsButtons', teamsAsButtons);
        else commit('setTeamsAsButtons', false);
        return localforage.getItem('rowsAsButtons')
      }).then(rowsAsButtons => {
        if (process.env.NODE_ENV === 'development') console.log('Store:authenticateDevice::set rows as buttons from local', rowsAsButtons)
        if (rowsAsButtons) commit('setRowsAsButtons', rowsAsButtons);
        else commit('setRowsAsButtons', false);
        return localforage.getItem('rows_order');
      }).then(rowsOrder => {
        if (process.env.NODE_ENV === 'development') console.log('Store:authenticateDevice::set rows order from local', rowsOrder);
        commit('setRowsOrder', rowsOrder);
        return localforage.getItem('activity_order')
      }).then(activityOrder => {
        if (process.env.NODE_ENV === 'development') console.log('Store:authenticateDevice::set activities from local', activityOrder);
        commit('setActivityOrder', activityOrder);
        return localforage.getItem('language')
      }).then(language => {
        if (process.env.NODE_ENV === 'development') console.log('Store:authenticateDevice::setLanguage from local', language);
        commit('setLanguage', language);
        setLanguage(language);

        return axios.post(config.api.baseUrl + '/authenticate/by-device', payload)
      }).then(response => {
        console.log('Store:authenticateDevice::authenticated', response.data.data);
        console.log('Store:authenticateDevice::authenticated -> overrideLocal', state.overrideLocal);
        dispatch('clearResources');

        const data = response.data.data;

        dispatch('setCurrentDevice', data.device);
        dispatch('setToken', data.token);
        dispatch('setCurrentLocation', data.location);
        dispatch('setTeams', data.teams);
        commit('storeTeamsOffline', data.teams);
        dispatch('setEmployees', data.employees);
        commit('storeEmployeesOffline', data.employees);
        dispatch('setActivities', data.activities);
        commit('storeActivitiesOffline', data.activities);
        dispatch('cacheActivityImages', data.activities)
        dispatch('setRows', data.rows);
        commit('storeRowsOffline', data.rows);
        dispatch('setPackagings', data.packagings);
        commit('storePackagingsOffline', data.packagings);
        dispatch('cachePackagingImages', data.packagings);
        dispatch('setPercentages', data.percentages);
        commit('storePercentagesOffline', data.percentages);

        dispatch('setWorkdaysFromLocal');
        dispatch('setTimesheetEntriesFromLocal');

        if (state.overrideLocal) {
          console.log('Override settings', data.device)
          commit('setActivityOrder', data.device.activity_order)
          commit('setRowsOrder', data.device.rows_order);
          commit('setRowsAsButtons', data.device.rows_as_buttons);
          commit('setPinVisible', data.device.pin_visible);
          commit('setDarkMode', data.device.dark_mode);
          commit('setTeamsOrder', data.device.teams_order);
          commit('setTeamsAsButtons', data.device.teams_as_buttons);
        } else {
          axios.put(config.api.current + '/device/settings', {
            'activities_order': state.activityOrder,
            'teams_as_buttons': state.teamsAsButtons,
            'teams_order': state.teamsOrder,
            'rows_as_buttons': state.rowsAsButtons,
            'rows_order': state.rowsOrder,
            'pin_visible': state.pinVisible,
            'dark_mode': state.darkMode,
          }, {headers: {'Authorization': 'Bearer ' + state.token}}).then(() => {
            console.log('Store:authenticateDevice::set overrideLocal to true')
            commit('overrideLocal', true)
          })
        }

        return loadAsyncLanguages(data.device.translations, data.device.language);
      }).catch(e => {
        if (e.response && e.response.status && e.response.status === 403) {
          window.events.$emit('error', 'errors.not_authorized');

          localforage.removeItem('token')
              .then(() => {
                dispatch('clearResources');
                return localforage.removeItem('currentDevice');
              })
              .then(() => {
                return localforage.removeItem('currentLocation');
              })
              .then(() => {
                window.events.$emit('redirectToLogin');
              });
        } else {
          window.events.$emit('authenticateLocalDevice');
        }
      });
    },

    sendFCMTokenToBackend ({commit, state}, token) {
      if (!state.fcmToken || state.fcmToken !== token || !state.fcmTokenSynced) {
        console.log('Store:dispatch::sendFCMTokenToBackend', token);
        commit('sendFCMTokenToBackend', token)
        state.fcmTokenSynced = false;

        if (isOnline()) {
          return localforage.getItem('token')
            .then(value => {
              if (!value) return false;

              return axios.put(config.api.current + '/device/fcm-token', {token: token}, {headers: { 'Authorization': 'Bearer ' + value }})
            })
            .then(res => {
              console.log('/device/fcm-token response', res === false ? 'Device not authenticated' : res)

              if (res !== false) state.fcmTokenSynced = true;

              return res;
            })
            .catch(e => {
              console.log('Store:dispatch::No token stored', {message: e, response: e.response})
              if (e.response && e.response.status) {
                if (e.response.status === 403) window.events.$emit('error', 'errors.fcm_token_not_saved');
              }

              state.fcmToken = null;
              localforage.removeItem('fcmToken');
            });
        }
      }
    },

    clearResources ({commit}) {
      console.log('Store::clearResources');
      commit('clearTeams');
      commit('clearEmployees');
      commit('clearActivities');
      commit('clearRows');
      commit('clearPackagings');
    },

    syncResources ({dispatch, commit, state}, instance) {
      console.log('Store::syncResources')
      const self = this;

      const currentLocation = state.currentDevice.location_id;

      if (state.enableTracking) writeTrackLog("Sync resources remote");

      return dispatch('flushPendingChanges')
        .then(() => axios.post(config.api.baseUrl + '/authenticate/by-device', state.currentDevice))
        .then(function(response) {
          dispatch('clearResources');

          const data = response.data.data;

          console.log('Device Store::syncResources:authenticated', state.overrideLocal, data);

          dispatch('setCurrentDevice', data.device);
          dispatch('setToken', data.token);
          dispatch('setCurrentLocation', data.location);
          dispatch('setTeams', data.teams);
          commit('storeTeamsOffline', data.teams);
          dispatch('setEmployees', data.employees);
          commit('storeEmployeesOffline', data.employees);
          dispatch('setActivities', data.activities);
          commit('storeActivitiesOffline', data.activities);
          dispatch('cacheActivityImages', data.activities);
          dispatch('setRows', data.rows);
          commit('storeRowsOffline', data.rows);
          dispatch('setPackagings', data.packagings);
          commit('storePackagingsOffline', data.packagings);
          dispatch('cachePackagingImages', data.packagings);
          dispatch('setPercentages', data.percentages);
          commit('storePercentagesOffline', data.percentages);

          dispatch('setWorkdaysFromLocal');
          dispatch('setTimesheetEntriesFromLocal');

          if (parseInt(data.device.location_id) !== parseInt(currentLocation)) commit('clearActivityOrder');

          if (state.overrideLocal) commit('setActivityOrder', data.device.activity_order);
          if (state.overrideLocal) commit('setRowsAsButtons', data.device.rows_as_buttons);
          if (state.overrideLocal) commit('setPinVisible', data.device.pin_visible);
          if (state.overrideLocal) commit('setDarkMode', data.device.dark_mode);
          if (state.overrideLocal) commit('setRowsOrder', data.device.rows_order);

          return loadAsyncLanguages(data.device.translations, data.device.language);
        })
        .then(() => {
          if (!instance.loadingData) self.dispatch('setChecked');

          window.setTimeout(() => {
            if (process.env.NODE_ENV === 'development') console.log('timeout ran');

            instance.syncing = false;
            instance.showInfo = false;

            return Promise.resolve(instance);
          }, 300);
        })
        .catch(function(e) {
          if (e.response && e.response.status) {
            if (e.response.status === 403) {
              window.events.$emit('error', 'errors.not_authorized');

              localforage.removeItem('token').then(() => {
                self.dispatch('clearResources');
                return localforage.removeItem('currentDevice');
              }).then(() => {
                return localforage.removeItem('currentLocation');
              }).then(() => {
                window.events.$emit('redirectToLogin');
              });
            }
          }

          instance.syncing = false;
        });
    },

    toggleRowsAsButtons({commit, state}) {
      commit('setRowsAsButtons', !state.rowsAsButtons);
      persistDeviceSetting(commit, state, 'rows_as_buttons', '/device/rows-as-buttons', {asButtons: state.rowsAsButtons});
    },

    setRowsAsButtons({commit, state}, val) {
      commit('setRowsAsButtons', val);
      persistDeviceSetting(commit, state, 'rows_as_buttons', '/device/rows-as-buttons', {asButtons: val});
    },

    togglePinVisible({commit, state}) {
      commit('setPinVisible', !state.pinVisible);
      persistDeviceSetting(commit, state, 'pin_visible', '/device/settings', {pin_visible: state.pinVisible});
    },

    setPinVisible({commit, state}, val) {
      commit('setPinVisible', val);
      persistDeviceSetting(commit, state, 'pin_visible', '/device/settings', {pin_visible: val});
    },

    toggleDarkMode({commit, state}) {
      commit('setDarkMode', !state.darkMode);
      persistDeviceSetting(commit, state, 'dark_mode', '/device/settings', {dark_mode: state.darkMode});
    },

    setDarkMode({commit, state}, val) {
      commit('setDarkMode', val);
      persistDeviceSetting(commit, state, 'dark_mode', '/device/settings', {dark_mode: val});
    },

    setRowsOrder({commit, state}, order) {
      commit('setRowsOrder', order);
      persistDeviceSetting(commit, state, 'rows_order', '/device/rows-order', {order: order});
    },

    toggleTeamsAsButtons({commit, state}) {
      commit('setTeamsAsButtons', !state.teamsAsButtons);
      persistDeviceSetting(commit, state, 'teams_as_buttons', '/device/teams-as-buttons', {asButtons: state.teamsAsButtons});
    },

    setTeamsAsButtons({commit, state}, val) {
      commit('setTeamsAsButtons', val);
      persistDeviceSetting(commit, state, 'teams_as_buttons', '/device/teams-as-buttons', {asButtons: val});
    },

    setTeamsOrder({commit, state}, order) {
      commit('setTeamsOrder', order);
      persistDeviceSetting(commit, state, 'teams_order', '/device/teams-order', {order: order});
    },

    flushPendingChanges ({commit, state}) {
      return flushPendingDeviceSettings(state).then(res => {
        // Everything we changed locally is now on the server, so it is safe to
        // let the server values win again on the upcoming sync.
        if (res.flushed > 0 && res.remaining === 0) commit('overrideLocal', true);
        return res;
      });
    },

    setChecked({commit}, payload) {
      commit('setChecked', payload);
    },

    setError ({commit}, payload){
      console.log('STORE::setError', payload)
      commit('setError', payload);
    },

    clearErrors ({commit}) {
      commit('setError', null);
    },

    setSuccess ({state}, payload) {
      state.success = payload;
    },

    clearSuccess ({state}) {
      state.success = null;
    },

    setCurrentDevice ({commit}, device) {
      commit('setCurrentDevice', device);
      localforage.setItem('currentDevice', JSON.parse(JSON.stringify(device)));
    },

    setToken ({commit}, payload) {
      commit('setToken', payload);
      localforage.setItem('token', payload);
    },

    setCurrentLocation ({commit}, payload) {
      commit('setCurrentLocation', payload);
      localforage.setItem('currentLocation', payload);
    },

    loadLocations ({commit}) {
      axios.get(config.api.current + '/locations&' + Math.random(), {headers: ['Authorization', this.state.token]})
          .then(function(response) {
            commit('setLocations', response.data.data);
          })
          .catch(function(error) {
            if (error.response.status === 401) commit('clearToken');
            commit('setError', error.response.data.error);
          })
    },

    setTeams ({commit}, payload) {
      window.teamStore.clear();
      commit('setTeams', payload);
    },

    loadEmployees ({commit}) {
      if (this.state.currentDevice === null || this.state.currentDevice === undefined) return;

      axios.get(config.api.current + '/api/v1/employees?client=' + this.state.currentDevice.location_id, {
        headers: {
          'Authorization': 'Bearer ' + this.state.token
        }
      }).then(function(response) {
        const data = response.data.data;
        const l = data.length;

        for (let i = 0; i < l; i++) {
          let employee = data[i];
          window.employeeStore.setItem(employee.id.toString(), employee);
        }
      })
          .catch(function(error) {
            commit('setError', error.response.data.error)
          })
    },

    setEmployee ({commit}, payload) {
      commit('setEmployee', payload);
    },

    setEmployees ({commit}, payload) {
      window.employeeStore.clear();
      commit('setEmployees', payload);
    },

    setEmployeesFromLocal ({commit}) {
      let employees = []
      return window.employeeStore
        .iterate(employee => {
          employees.push(employee)
        })
        .then(() => {
          commit('setEmployees', employees)
          return employees;
        });
    },

    setCurrentEmployee ({commit}, payload) {
      commit('setCurrentEmployee', payload);

      if (payload.lang !== document.documentElement.lang) setLanguage(payload.lang)

    },

    clearCurrentEmployee ({commit, state}) {
      if (document.documentElement.lang !== state.currentDevice.language) { setLanguage(state.currentDevice.language) }
      commit('clearCurrentEmployee')
    },

    setActivities ({commit}, payload) {
      window.activityStore.clear();
      commit('setActivities', payload)
    },

    setActivitiesFromLocal ({commit}) {
      let activities = []

      return window.activityStore
        .iterate(activity => {
          activities.push(activity)
        })
        .then(() => {
          commit('setActivities', activities)
          return activities
        })
    },

    cacheActivityImages({commit}, payload) {
      payload.forEach(activity => {
        if (typeof cordova !== 'undefined' && activity.image_path && !activity.image_path.startsWith('http://via.placeholder.com')) {
          FileCache.remember(activity.image_path);
        }
      })
    },

    setRows ({commit}, payload) {
      window.rowStore.clear()
      commit('setRows', payload)
      this.commit('storeRowsOffline', payload)
    },

    setRowsFromLocal ({commit}) {
      let rows = []

      return window.rowStore
        .iterate(row => {
          rows.push(row)
        })
        .then(() => {
          commit('setRows', rows);
          return rows;
        })
    },

    setPackagings ({commit}, payload) {
      window.packagingsStore.clear()
      commit('setPackagings', payload)
    },

    setPackagingsFromLocal ({commit}) {
      let packagings = [];

      return window.packagingsStore
        .iterate(packaging => {
          packagings.push(packaging)
        })
        .then(() => {
          commit('setPackagings', packagings);
          return packagings;
        })
    },

    cachePackagingImages({commit}, payload) {
      payload.forEach(packaging => {
        if (typeof cordova !== 'undefined' && packaging.image_path && !packaging.image_path.startsWith('http://via.placeholder.com')) {
          FileCache.remember(packaging.image_path);
        }
      })
    },

    setPercentages ({commit}, payload) {
      window.percentagesStore.clear()
      commit('setPercentages', payload)
    },

    setPercentagesFromLocal ({commit}) {
      let percentages = [];

      return window.percentagesStore
        .iterate(percentage => {
          percentages.push(percentage)
        })
        .then(() => {
          commit('setPercentages', percentages);
          return percentages;
        })
    },

    loadWorkdays ({commit}) {
      if (this.state.currentLocation === null || this.state.currentLocation === undefined) {
        return
      }
      axios.get(config.api.current + '/workdays', {headers: {
          'Authorization': 'Bearer ' + this.state.token
        }})
          .then(function(response) {
            const data = response.data.data
            commit('setWorkdays', data)
            self.dispatch('storeWorkdaysOffline', data)
          })
          .catch(function(error) {
            commit('setError', error.response.data.error)
          })
    },

    setWorkdays ({commit}, payload) {
      commit('setWorkdays', payload)
    },

    setWorkdaysFromLocal ({commit}) {
      let workdays = [];

      return window.workdayStore
        .iterate(value => {
          workdays.push(value)
        })
        .then(() => {
          commit('setWorkdays', workdays)
          return workdays;
        });
    },

    deleteWorkday ({commit}, payload) {
      return axios.delete(config.api.current + '/workday/' + payload.id, {headers: {
          'Authorization': 'Bearer ' + this.state.token
        }})
          .then(() => {
            return window.workdayStore.removeItem(payload.id.toString());
          })
          .then(() => {
            commit('deleteWorkday', payload);

            return Promise.resolve();
          })
    },

    startWorkday ({commit, state}) {
      if(state.currentEmployee !== null && state.currentEmployee !== undefined) {
        const newWorkday = {
          id: v4(),
          'location_id': state.currentLocation.id,
          'employee_id': state.currentEmployee.id,
          start: new moment().toISOString(),
          end: null,
          activities: [],
          breaks: [],
          synced: false
        }
        commit('createWorkday', newWorkday)
      }
    },

    endWorkday ({state}) {
      if(state.currentEmployee !== null && state.currentEmployee !== undefined) {
        const end = new moment().toISOString();
        const workday = this.getters.currentWorkday;
        workday.end = end;
        workday.synced = false;

        window.workdayStore.getItem(workday.id.toString())
            .then(function(item) {
              item.end = end;
              item.synced = false;

              window.workdayStore.setItem(item.id.toString(), item)
            });

        if (isOnline()) {
          const store = this;

          saveNewWorkday(workday, store);
        }
      }

    },

    setTimesheetEntriesFromLocal ({commit}) {
      let entries = [];

      return window.timesheetStore
        .iterate(value => {
          entries.push(value)
        })
        .then(() => {
          if (entries.length > 0) commit('setTimesheetEntries', entries)
          return entries;
        });
    },

    deleteTimesheetEntry ({commit}, payload) {
      return axios.delete(config.api.current + '/timesheet-entry/' + payload.id, {headers: {
          'Authorization': 'Bearer ' + this.state.token
        }})
          .then(() => {
            return window.timesheetStore.removeItem(payload.id.toString());
          })
          .then(() => {
            commit('deleteTimesheetEntry', payload);

            return Promise.resolve();
          })
    },

    async registerActivity ({commit, state}, payload) {
      if (process.env.NODE_ENV === 'development') console.log('registerActivity', payload);
      if (!state.currentEmployee) return {};

      const newActivity = {
        'id': v4(),
        'workday_id': this.getters.currentWorkday ? this.getters.currentWorkday.id: null,
        'employee_id': state.currentEmployee.id,
        'location_id': this.getters.currentLocation.id,
        'client_id': this.getters.currentLocation.client_id,
        'type': payload.type,
        'start': payload.start ? payload.start : null,
        'end': null,
        'activity_id': payload.activity_id,
        'packaging': null,
        'row': null,
        'row_id': null,
        'row_status': null,
        'row_percentage': null,
        'amount': null,
        'on_hold': false,
        'synced': false
      };

      if (this.getters.debug) writeLog("register activity: " + JSON.stringify(newActivity));

      commit('storeActivity', newActivity);

      return newActivity;
    },

    startActivity ({commit, state}, payload) {
      if(state.currentEmployee !== null && state.currentEmployee !== undefined) {
        const workdayId = this.getters.currentWorkday ? this.getters.currentWorkday.id: null
        const newActivity = {
          'id': v4(),
          'workday_id': workdayId,
          'employee_id': state.currentEmployee.id,
          'location_id': this.getters.currentLocation.id,
          'type': payload.type,
          'start': new moment().toISOString(),
          'end': null,
          'activity_id': payload.activity_id,
          'packaging': null,
          'row': null,
          'row_id': null,
          'row_status': null,
          'row_percentage': null,
          'amount': null,
          'on_hold': false,
          'synced': false
        };

        if (this.getters.debug) writeLog("start activity: " + JSON.stringify(newActivity));

        commit('storeActivity', newActivity);
      }
    },

    updateActivity ({commit}, payload) {
      commit('updateActivity', payload);
    },

    updateTimesheetEntry ({commit}, payload) {
      commit('updateTimesheetEntry', payload);
    },

    clearDatabase ({commit}) {
      commit('clearEmployees');
      commit('clearActivities');
      commit('clearRows');
      commit('clearPackagings');
      commit('clearWorkdays');
      commit('clearTimesheetEntries');
    },

    cancelActivity ({commit, state}, timesheetEntryId) {
      const entry = state.timesheetEntries.find(entry => entry.id === timesheetEntryId);

      if (isOnline() && Number.isInteger(entry.id)) {
        const cnf = { headers: { 'Authorization': 'Bearer ' + state.token } };

        axios.delete(config.api.current + '/timesheet-entry/' + entry.id, cnf)
            .then(() => commit('cancelActivity', entry))
            .catch(() => commit('cancelActivity', entry));
      } else {
        commit('cancelActivity', entry);
      }
    },

    setActivityOrder ({commit, state}, newOrder) {
      commit('setActivityOrder', newOrder);
      persistDeviceSetting(commit, state, 'activity_order', '/device/activity-order', {order: newOrder});
    }
  },

  getters: {
    debug (state) {
      return state.currentDevice && state.currentDevice.debug
    },
    online (state) {
      return state.online || false
    },
    batteryLevel (state) {
      return state.batteryLevel || 0
    },
    batteryCharging (state) {
      return state.batteryCharging || false
    },
    enableTracking (state) {
      return state.currentDevice && state.enableTracking
    },
    locationById (state) {
      return id => state.locations.find((location) => location.id === id);
    },
    currentLocation (state) {
      return state.currentLocation
    },
    employees (state) {
      return state.employees
    },
    employeeByPin (state) {
      return pin => state.employees.find((employee) => employee.pin === pin);
    },
    currentEmployee (state) {
      return state.currentEmployee
    },
    currentEmployeeActivities (state) {
      return state.currentEmployeeActivities
    },

    activities (state) {
      if (!state.activities) return null;

      return state.activities
          .map(activity => {
            if (!activity.order) activity.order = null;

            return activity
          })
          .sort((activityA, activityB) => {
            if (activityA.name.toLowerCase() < activityB.name.toLowerCase()) return -1;
            if (activityA.name.toLowerCase() > activityB.name.toLowerCase()) return 1;
            return 0
          });
    },

    orderedActivities (state) {
      if (!state.activities) return [];

      return state.activities.filter(activity => activity.order !== null && activity.order !== undefined)
          .sort((activityA, activityB) => {
            if (activityA.order < activityB.order) return 1;
            if (activityA.order > activityB.order) return -1;
            return 0;
          })
    },

    activityById (state) {
      return (activityId) => {
        return state.activities.find((activity) => {
          return activity.id === parseInt(activityId, 10)
        })
      }
    },

    currentActivity (state) {
      return state.currentActivity
    },

    currentActivityForEmployee (state) {
      if (!state.currentEmployee || !state.timesheetEntries) return null;

      return state.timesheetEntries.find(entry =>
          entry.employee_id === state.currentEmployee.id && entry.type === 1 && entry.end === null);
    },

    currentBreakForEmployee (state) {
      if (state.currentEmployee !== null && state.timesheetEntries !== null) {
        return state.timesheetEntries.find(function (entry) {
          return entry.employee_id === state.currentEmployee.id && entry.type === 2 && entry.end === null
        })
      }

      return null
    },

    teamByTitle (state) {
      return (teamName) => state.teams.find((team) => team.name.toString() === teamName.toString());
    },

    rows (state) {
      return state.rows
    },

    rowByTitle (state) {
      return (rowId) => {
        return state.rows.find((row) => {
          return row.title === rowId
        })
      }
    },

    packagings (state) {
      if (!state.packagings) {
        return null
      }
      return state.packagings.sort((packagingA, packagingB) => {
        if (packagingA.name.toLowerCase() < packagingB.name.toLowerCase()) return -1;
        if (packagingA.name.toLowerCase() > packagingB.name.toLowerCase()) return 1;
        return 0
      })
    },

    packagingById (state) {
      return (packagingId) => {
        return state.packagings.find((packaging) => {
          return packaging.id === parseInt(packagingId, 10)
        })
      }
    },

    workdays (state) {
      return state.workdays
    },

    workdayById (state) {
      return (workdayId) => {
        return state.workdays.find((workday) => {
          return workday.id === workdayId
        })
      }
    },

    timesheetEntryById (state) {
      return (timesheetEntryId) => state.timesheetEntries.find((entry) => entry.id === timesheetEntryId);
    },

    currentWorkday (state) {
      if(state.currentEmployee === null || state.currentEmployee === undefined || state.workdays === null) {
        return null;
      }

      if (state.workdays && Array.isArray(state.workdays)) {
        return state.workdays.find(workday => workday.employee_id === state.currentEmployee.id && workday.end === null);
      }

      return null;
    },

    currentDevice (state) {
      return state.currentDevice;
    },

    instantSync (state) {
      return state.instantSync;
    },

    activityOrder (state) {
      return state.activityOrder;
    },

    orderedRows (state) {
      if (!state.rows) return [];

      return state.rows.filter(row => row.order !== null && row.order !== undefined)
          .sort((rowA, rowB) => {
            if (rowA.order < rowB.order) return 1;
            if (rowA.order > rowB.order) return -1;
            return 0
          });
    },

    orderedTeams (state) {
      if (!state.teams) return [];

      return state.teams.filter(team => team.order !== null && team.order !== undefined)
          .sort((teamA, teamB) => {
            if (teamA.order < teamB.order) return 1;
            if (teamA.order > teamB.order) return -1;
            return 0
          })
    }
  },

  modules: {
  }
});

export default appStore;
